import React, { useState } from "react";
import SurveyQuestionDialog from "components/Dialogs/SurveyQuestionDialog";
import {
  CPSQuestionResponse, QuestionControlResponse
} from "commonUtils/genericInterfaces";
import { store } from "./../../../redux/store";

import {
  getRequest, setHeader,
  postRequest
} from "repository/genericRepository";

import {
  FETCH_SURVEY_QUESTION,
  SURVEY_QUESTION_CONTROL,
  SAVE_SURVEY_QUESTIONS,
} from "commonUtils/globalAPISettings";
import {
  isValidObject,
  loggedMessage,
} from "commonUtils/utils";
import {
  API_TIMEOUTS_MILLISECONDS,
  CPSConstants,
  PageNames,
  SurveyQuestionTypes,
} from "commonUtils/enumerations";
import { Loader } from "components/FormComponents/Loader/Loader";

type survey = {
  surveyId: number;
  handlePopUpClose: () => void;
};

type additionalQuestionDetailList = {
  responseOptionId: number,
  displayOrder: number,
  responseId: number,
  questionTextHTML: string,
  responseText: string
}
type MCQTypeQuestion = {
  fieldId: number;
  fieldLabel: string;
  displayToolTip: boolean,
  toolTipText: string,
}
type CPSSurveyResponse = {
  isSkipped: boolean,
  surveyID: number,
  responseId: number,
  responseOptionId: number,
  webDisplayOrder: number,
  targetUserID: number,
  responseText: string,
  questionType: string,
  profileId: number,
  additionalQuestionDetailList: additionalQuestionDetailList[],
  mcqQuestion?: MCQTypeQuestion[],
  followUpResponseText?: string,
  otherOptionMCQText?: string
  MCQResponseOptionsId?: string
};


export default function CPSSurvey({ surveyId, handlePopUpClose }: survey) {
  const [loader, setLoader] = React.useState(false);
  const [questionDetails, setQuestionDetails] = React.useState<CPSQuestionResponse>();
  const [openPreviewDialog, setOpenPreviewDialog] = React.useState<boolean>(false);
  const [questionControlData, setQuestionControlData] = React.useState<QuestionControlResponse>();
  const [errorCode, setErrorCode] = React.useState<number>(0);
  React.useEffect(() => {
    if (surveyId) {
      fetchSurveyQuestionAndControls(surveyId);
    }
  }, [surveyId]);

  const closeSurveyPopup = () => {
    setOpenPreviewDialog(false);
    setLoader(false);
    handlePopUpClose();
  }

  React.useEffect(() => {
  }, [questionDetails]);

  const saveAndFetchSurveyQuestionDetail = async (surveyResponse: CPSSurveyResponse) => {
    try {
      setLoader(true);
      const authState = store.getState();
      const APIHeaders = {
        Authorization: `Bearer ${authState?.auth?.authDetails?.token}`,
      };
      const responseData = await postRequest({
        url: `${SAVE_SURVEY_QUESTIONS}`,
        headers: setHeader(APIHeaders),
        params: {
          ...surveyResponse,
        },
      });

      if (isValidObject(responseData)) {
        if (responseData.isSuccessMessage) {
          if (surveyResponse.responseOptionId == 36) {
            closeSurveyPopup();
          } else {
            fetchAndShowQuestion(1, surveyResponse.webDisplayOrder);
          }
        } else if (responseData.isErrorMessage || responseData.isValidationMessage) {
          setErrorCode(7966975);
          setOpenPreviewDialog(true);
          setLoader(false);
        }
      }
    } catch (error: any) {
      setErrorCode(7966975);
      setOpenPreviewDialog(true);
    }
  };

  const fetchSurveyQuestionAndControls = async (surveyId: number) => {
    try {
      const authState = store.getState();
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      setErrorCode(0);
      const profileId = authState?.auth?.userDetails?.profileId;
      const response = await Promise.allSettled([
        getRequest({
          url: `${SURVEY_QUESTION_CONTROL}`,
          headers: setHeader(APIHeaders),
          timeout: API_TIMEOUTS_MILLISECONDS.SURVEY_QUESTION_CONTROL as number,
        }),
        postRequest({
          url: `${FETCH_SURVEY_QUESTION}`,
          headers: setHeader(APIHeaders),
          params: {
            surveyId,
            WebDisplayOrder: 0,
            profileId,
            navFlag: 0,
          },
        })
      ]);

      if (response[0].status === 'fulfilled') {
        const responseData = response[0].value;
        if (isValidObject(responseData)) {
          setQuestionControlData(responseData);
        }
      } else if (response[0].status === 'rejected') {
        handleErrors(response[0].reason, 7966974);
      }

      if (response[1].status === 'fulfilled' && isValidObject(response[1].value)) {
        setQuestionDetails(response[1].value);
        setOpenPreviewDialog(true);
      } else if (response[1].status === 'rejected') {
        handleErrors(response[1].reason, 7966974);
        setOpenPreviewDialog(true);
      }
    }
    catch (error) {
      handleErrors(error, 7966974);
      setOpenPreviewDialog(true);
    }
    return true;
  };

  const handleErrors = (error: any, code: number) => {
    setErrorCode(code);
    loggedMessage(error, "", PageNames.DASHBOARD);
    setLoader(false);
  };

  const fetchAndShowQuestion = async (navFlag: number, webDisplayOrder: number) => {
    try {
      const authState = store.getState();
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      setErrorCode(0);
      const profileId = authState?.auth?.userDetails?.profileId;
      const responseData = await postRequest({
        url: `${FETCH_SURVEY_QUESTION}`,
        headers: setHeader(APIHeaders),
        params: {
          surveyId,
          WebDisplayOrder: webDisplayOrder,
          profileId,
          navFlag: navFlag,
        },
      })
      if (isValidObject(responseData)) {
        let response = responseData;
        setQuestionDetails(response);
        setLoader(false);
        setOpenPreviewDialog(true);
      } else {
        setErrorCode(7966974);
        setLoader(false);
      }
    } catch (error) {
      handleErrors(error, 7966974);
    }
  };

  const saveAndFetchNextQuestion = async (questionDetail: CPSQuestionResponse) => {
    setErrorCode(0);
    if (questionDetail.showNotif) {
      closeSurveyPopup();
    } else {

      const isMCQ =
        questionDetail.questionType === SurveyQuestionTypes.SingleSelectMCQ ||
        questionDetail.questionType === SurveyQuestionTypes.MultiSelectMCQ;

      if (isMCQ) {
        const mcqIds = (questionDetail.mcqResponseOptionsId ?? "").trim();

        if (mcqIds === "") {
          const selectedLabels = (questionDetail.responseText ?? "")
            .split(",")
            .map(x => x.trim())
            .filter(Boolean);

          const ids =
            questionDetail.mcqQuestion
              ?.filter(o => selectedLabels.includes(o.fieldLabel))
              .map(o => String(o.fieldId)) ?? [];

          questionDetail.mcqResponseOptionsId = ids.join(",");
        }
      }
      const hasGridAnswer =
        (questionDetail.additionalQuestionDetailList ?? []).some(
          r =>
            (r.responseOptionId ?? 0) !== 0 ||
            (r.responseText ?? "").trim() !== ""
        );

      const isSkipped =
        questionDetail.responseOptionId == 0 &&
        (questionDetail.responseText ?? "").trim() === "" &&
        (questionDetail.mcqResponseOptionsId ?? "").trim() === "" &&
        (questionDetail.followUpResponseText ?? "").trim() === "" &&
        (questionDetail.otherOptionMCQText ?? "").trim() === "" &&
        !hasGridAnswer;

      const surveyResponse: CPSSurveyResponse = {
        profileId: questionDetail.profileId,
        isSkipped:
          isSkipped,
        surveyID: questionDetail.surveyID,
        webDisplayOrder: questionDetail.webDisplayOrder,
        targetUserID: questionDetail.targetUserID,
        responseId: questionDetail.responseId,
        responseOptionId: questionDetail.responseOptionId,
        responseText: questionDetail.responseText,
        questionType: questionDetail.questionType,
        additionalQuestionDetailList: questionDetail.additionalQuestionDetailList,
        mcqQuestion: questionDetail.mcqQuestion,
        followUpResponseText: questionDetail.followUpResponseText,
        otherOptionMCQText: questionDetail.otherOptionMCQText,
        MCQResponseOptionsId: questionDetail.mcqResponseOptionsId
      };

      await saveAndFetchSurveyQuestionDetail(surveyResponse);
    }
  };

  const handlePreviousClick = (questionDetail: CPSQuestionResponse) => {
    setLoader(true);
    setErrorCode(0);
    fetchAndShowQuestion(-1, questionDetails?.webDisplayOrder ?? 0);
  };

  return (
    <>
      <Loader open={loader} />
      {openPreviewDialog && questionDetails && (
        <SurveyQuestionDialog
          errorCode={errorCode}
          loader={loader}
          openPopup={openPreviewDialog}
          handlePopupClose={() => { closeSurveyPopup(); }}
          popUpTitle={'CPS User Survey'}
          handleSubmit={(qa: CPSQuestionResponse) => saveAndFetchNextQuestion(qa)}
          questionDetail={questionDetails}
          questionControlData={questionControlData}
          handlePrevious={() => { handlePreviousClick(questionDetails) }}
        />)}
    </>
  );
}
